<script setup lang="ts">
import { useRoute, useRouter } from 'vue-router';
import { computed } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { useI18n } from '@/composables/useI18n';
import { logoutRequest } from '@/util/requests';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const { resetPreviousNavigation } = useAuthStore();

const excludeLogoutButtonRoutes = ['Login'];

const excludedBreadcrumbRoutes = ['Login', 'NoProviderRoute', 'LoadingPage'];

const title = computed<string>(() => {
  return (route.meta.title as string) || (route.name as string) || '';
});

const isDashboardRoute = computed(() => {
  return route.name === 'Dashboard';
});

const showLogoutButton = computed(() => {
  return !excludeLogoutButtonRoutes.includes(route.name as string);
});

async function goBack() {
  const parentRoute = (route.meta.parentRoute as string) || 'Dashboard';
  await router.push({ name: parentRoute });
}

async function goToDashboard() {
  await router.push('/dashboard');
}

const showBreadcrumbs = computed(() => {
  return !excludedBreadcrumbRoutes.includes(route.name as string);
});

async function logout() {
  resetPreviousNavigation();
  await logoutRequest();
  router.go(0);
}
</script>

<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Header -->
    <header class="bg-white shadow">
      <div class="px-4 py-6 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div class="flex items-center justify-between">
          <h1
            class="flex items-center gap-2 text-2xl font-bold text-gray-800 sm:text-3xl md:text-4xl"
          >
            <span
              class="inline-block w-1.5 h-8 rounded-full mr-1 bg-blue-500"
            ></span>
            {{ t(title) }}
          </h1>
          <button
            v-if="showLogoutButton"
            @click="logout"
            class="inline-flex items-center px-4 py-2 text-sm font-medium text-white transition-colors duration-150 bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
          >
            <svg
              class="w-5 h-5 mr-2"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fill-rule="evenodd"
                d="M3 3a1 1 0 011 1v12a1 1 0 11-2 0V4a1 1 0 011-1zm7.707 3.293a1 1 0 010 1.414L9.414 9H17a1 1 0 110 2H9.414l1.293 1.293a1 1 0 01-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0z"
                clip-rule="evenodd"
              />
            </svg>
            {{ t('common.logout') }}
          </button>
        </div>
      </div>
    </header>

    <!-- Main Content -->
    <main class="px-4 py-6 mx-auto max-w-7xl sm:px-6 lg:px-8">
      <div class="flex gap-2 mb-4" v-if="showBreadcrumbs">
        <button
          v-if="!isDashboardRoute"
          @click="goBack"
          :class="
            isDashboardRoute
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-white text-gray-700 hover:bg-gray-50'
          "
          :disabled="isDashboardRoute"
          class="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"
        >
          <svg
            class="w-5 h-5 mr-2"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
              clip-rule="evenodd"
            />
          </svg>
          {{ t('common.back') }}
        </button>

        <button
          @click="goToDashboard"
          class="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"
        >
          <svg
            class="w-5 h-5 mr-2"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z"
            />
          </svg>
          {{ t('common.home') }}
        </button>
      </div>

      <router-view />
    </main>
  </div>
</template>
